import { useEffect, useState } from 'react';
import { IconBox, IconCheck, IconEdit, IconPlus, IconSearch, IconTrash } from '../components/icons';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, StatCard, Toggle, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCurrency } from '../lib/format';

const KINDS: Record<string, string> = {
  central: 'Zentrallager',
  vehicle: 'Fahrzeuglager',
  property: 'Objektlager',
};

/**
 * Lagerstammdaten (Kap. 7).
 *
 * Ein Lager ist hier ein vollwertiger Stammdatensatz wie ein Objekt: mit
 * Anschrift, Koordinaten, Ansprechpartner und Zugangshinweisen. Der Fahrer,
 * der Material holen soll, muss wissen, wohin er fährt und wie er hineinkommt.
 */
export function WarehousesPage() {
  const { can, user } = useAuth();
  const { notify } = useToast();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const warehouses = useAsync(
    () => api.get('/api/material/warehouses', { includeInactive: showInactive ? 'true' : '' }),
    [showInactive],
  );
  const users = useAsync(() => api.get('/api/users').catch(() => []), []);
  const properties = useAsync(() => api.get('/api/properties').catch(() => []), []);

  const mayManage = can('material.warehouses');

  const list = (warehouses.data ?? []).filter((warehouse: any) => {
    if (!search.trim()) return true;
    const haystack = `${warehouse.name} ${warehouse.code ?? ''} ${warehouse.city ?? ''} ${warehouse.street ?? ''}`;
    return haystack.toLowerCase().includes(search.toLowerCase());
  });

  const remove = async (warehouse: any, permanent: boolean) => {
    const question = permanent
      ? `Lager „${warehouse.name}“ endgültig löschen? Nur möglich, solange kein Bestand und keine Bewegungen daran hängen.`
      : `Lager „${warehouse.name}“ stilllegen? Bestand und Historie bleiben erhalten.`;
    if (!window.confirm(question)) return;
    try {
      await api.delete(`/api/material/warehouses/${warehouse.id}`, permanent ? { permanent: 'true' } : undefined);
      notify(permanent ? 'Lager gelöscht.' : 'Lager stillgelegt.', 'success');
      warehouses.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion nicht möglich.', 'error');
    }
  };

  const reactivate = async (warehouse: any) => {
    await api.post(`/api/material/warehouses/${warehouse.id}/reactivate`);
    notify('Lager wieder aktiv.', 'success');
    warehouses.reload();
  };

  const totalValue = list.reduce((sum: number, entry: any) => sum + (entry.stock_value ?? 0), 0);

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconBox width={22} height={22} />}
        title="Lager"
        subtitle="Zentral-, Fahrzeug- und Objektlager mit Anschrift, Ansprechpartner und Zugangshinweisen. Jedes Lager ist Ziel von Wareneingängen, Entnahmen und Inventuren."
        pill="Stammdaten"
        tone="warning"
        actions={
          mayManage ? (
            <button className="btn primary" onClick={() => setEditing({ isNew: true })}>
              <IconPlus width={15} height={15} /> Lager anlegen
            </button>
          ) : undefined
        }
      />

      <div className="grid cols-4">
        <StatCard label="Lager" value={list.length} tone="primary" icon={<IconBox width={17} height={17} />} hint="aktiv im Zugriff" />
        <StatCard
          label="Zentrallager"
          value={list.filter((entry: any) => entry.kind === 'central').length}
          tone="info"
          icon={<IconBox width={17} height={17} />}
        />
        <StatCard
          label="Fahrzeuglager"
          value={list.filter((entry: any) => entry.kind === 'vehicle').length}
          tone="accent"
          icon={<IconBox width={17} height={17} />}
        />
        <StatCard
          label="Bestandswert"
          value={formatCurrency(totalValue)}
          tone="success"
          icon={<IconBox width={17} height={17} />}
          hint="zum Einkaufspreis"
        />
      </div>

      <Card noBody>
        <div className="card-body tight">
          <div className="row">
            <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380 }}>
              <input
                type="search"
                placeholder="Lager, Kürzel oder Ort suchen …"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: 34 }}
              />
              <span style={{ position: 'absolute', left: 11, top: 10, color: 'var(--text-subtle)' }}>
                <IconSearch width={15} height={15} />
              </span>
            </div>
            <label className="checkbox-row">
              <input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
              Stillgelegte anzeigen
            </label>
          </div>
        </div>

        {warehouses.loading ? (
          <Loading />
        ) : list.length === 0 ? (
          <EmptyState icon={<IconBox width={20} height={20} />} title="Kein Lager gefunden" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Lager</th>
                  <th>Art</th>
                  <th>Anschrift</th>
                  <th>Zuordnung</th>
                  <th className="num">Artikel</th>
                  <th className="num">Bestandswert</th>
                  {mayManage && <th className="actions">Aktionen</th>}
                </tr>
              </thead>
              <tbody>
                {list.map((warehouse: any) => (
                  <tr key={warehouse.id} style={warehouse.is_active ? undefined : { opacity: 0.55 }}>
                    <td>
                      <strong>{warehouse.name}</strong>
                      {warehouse.is_default ? (
                        <span style={{ marginLeft: 8 }}>
                          <Badge variant="success">Vorgabe</Badge>
                        </span>
                      ) : null}
                      {!warehouse.is_active && (
                        <span style={{ marginLeft: 8 }}>
                          <Badge>Stillgelegt</Badge>
                        </span>
                      )}
                      <div className="small subtle mono">{warehouse.code ?? '–'}</div>
                    </td>
                    <td className="small">
                      <span className="chip">{KINDS[warehouse.kind] ?? warehouse.kind}</span>
                    </td>
                    <td className="small">
                      {[warehouse.street, warehouse.house_no].filter(Boolean).join(' ') || '–'}
                      <div className="subtle">
                        {[warehouse.zip, warehouse.city].filter(Boolean).join(' ')}
                      </div>
                    </td>
                    <td className="small">
                      {warehouse.kind === 'vehicle' && warehouse.first_name
                        ? `${warehouse.first_name} ${warehouse.last_name}`
                        : warehouse.kind === 'property' && warehouse.property_name
                          ? warehouse.property_name
                          : (warehouse.contact_name ?? '–')}
                    </td>
                    <td className="num">{warehouse.article_count}</td>
                    <td className="num">{formatCurrency(warehouse.stock_value ?? 0)}</td>
                    {mayManage && (
                      <td className="actions">
                        <button className="btn sm" onClick={() => setEditing(warehouse)}>
                          <IconEdit width={13} height={13} /> Bearbeiten
                        </button>
                        {warehouse.is_active ? (
                          <>
                            <button
                              className="btn sm ghost"
                              style={{ marginLeft: 6 }}
                              title="Stilllegen – Bestand und Historie bleiben erhalten"
                              onClick={() => remove(warehouse, false)}
                            >
                              Stilllegen
                            </button>
                            <button
                              className="btn sm ghost"
                              title="Endgültig löschen – nur ohne Bestand und Historie"
                              onClick={() => remove(warehouse, true)}
                            >
                              <IconTrash width={13} height={13} />
                            </button>
                          </>
                        ) : (
                          <button className="btn sm ghost" style={{ marginLeft: 6 }} onClick={() => reactivate(warehouse)}>
                            <IconCheck width={13} height={13} /> Aktivieren
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <WarehouseModal
          warehouse={editing}
          users={users.data ?? []}
          properties={properties.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            warehouses.reload();
            notify(editing.isNew ? 'Lager angelegt.' : 'Lager gespeichert.', 'success');
          }}
        />
      )}
    </div>
  );
}

function WarehouseModal({
  warehouse,
  users,
  properties,
  onClose,
  onSaved,
}: {
  warehouse: any;
  users: any[];
  properties: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = Boolean(warehouse.isNew);
  const [form, setForm] = useState<any>({
    name: '',
    code: '',
    kind: 'central',
    userId: '',
    propertyId: '',
    street: '',
    houseNo: '',
    zip: '',
    city: '',
    latitude: '',
    longitude: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    accessNotes: '',
    note: '',
    isDefault: false,
    isActive: true,
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setForm({
      name: warehouse.name ?? '',
      code: warehouse.code ?? '',
      kind: warehouse.kind ?? 'central',
      userId: warehouse.user_id ? String(warehouse.user_id) : '',
      propertyId: warehouse.property_id ? String(warehouse.property_id) : '',
      street: warehouse.street ?? '',
      houseNo: warehouse.house_no ?? '',
      zip: warehouse.zip ?? '',
      city: warehouse.city ?? '',
      latitude: warehouse.latitude != null ? String(warehouse.latitude) : '',
      longitude: warehouse.longitude != null ? String(warehouse.longitude) : '',
      contactName: warehouse.contact_name ?? '',
      contactPhone: warehouse.contact_phone ?? '',
      contactEmail: warehouse.contact_email ?? '',
      accessNotes: warehouse.access_notes ?? '',
      note: warehouse.note ?? '',
      isDefault: Boolean(warehouse.is_default),
      isActive: warehouse.is_active !== 0,
    });
  }, [warehouse, isNew]);

  const set = (key: string, value: unknown) => setForm((current: any) => ({ ...current, [key]: value }));

  const locate = async () => {
    const query = [form.street, form.houseNo, form.zip, form.city].filter(Boolean).join(' ');
    if (query.length < 5) return;
    try {
      const hits = await api.get('/api/routing/geocode', { q: query });
      if (hits.length > 0) setForm((current: any) => ({ ...current, latitude: String(hits[0].lat), longitude: String(hits[0].lng) }));
    } catch {
      /* Der Adressdienst ist optional – Koordinaten lassen sich auch eintippen. */
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        ...form,
        userId: form.kind === 'vehicle' && form.userId ? Number(form.userId) : null,
        propertyId: form.kind === 'property' && form.propertyId ? Number(form.propertyId) : null,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
      };
      if (isNew) await api.post('/api/material/warehouses', payload);
      else await api.patch(`/api/material/warehouses/${warehouse.id}`, payload);
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isNew ? 'Lager anlegen' : `Lager bearbeiten: ${warehouse.name}`}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" onClick={save} disabled={busy || form.name.trim().length < 2}>
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />

        <div className="form-grid">
          <Field label="Bezeichnung" required>
            <input value={form.name} autoFocus onChange={(event) => set('name', event.target.value)} />
          </Field>
          <Field label="Kürzel" hint="Kurzzeichen für Belege, z. B. LG-100.">
            <input value={form.code} onChange={(event) => set('code', event.target.value)} />
          </Field>
          <Field label="Art">
            <select value={form.kind} onChange={(event) => set('kind', event.target.value)}>
              {Object.entries(KINDS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          {form.kind === 'vehicle' && (
            <Field label="Fahrzeug von" hint="Das Lager reist mit dieser Person.">
              <SearchSelect
                value={String(form.userId ?? '')}
                onChange={(wert) => set('userId', wert)}
                options={(users ?? []).map((person: any) => ({ value: String(person.id), label: `${person.first_name} ${person.last_name}` }))}
                placeholder="Ohne Zuordnung"
              />
            </Field>
          )}

          {form.kind === 'property' && (
            <Field label="Objekt">
              <SearchSelect
                value={String(form.propertyId ?? '')}
                onChange={(wert) => set('propertyId', wert)}
                options={(properties ?? []).map((property: any) => ({ value: String(property.id), label: String(property.name) }))}
                placeholder="Ohne Zuordnung"
              />
            </Field>
          )}

        </div>

        <span className="section-title">Anschrift</span>
        <div className="address-grid">
          <Field label="Straße">
            <input value={form.street} onChange={(event) => set('street', event.target.value)} />
          </Field>
          <Field label="Nr.">
            <input value={form.houseNo} onChange={(event) => set('houseNo', event.target.value)} />
          </Field>
          <Field label="PLZ">
            <input value={form.zip} inputMode="numeric" onChange={(event) => set('zip', event.target.value)} />
          </Field>
          <Field label="Ort">
            <input value={form.city} onChange={(event) => set('city', event.target.value)} />
          </Field>
        </div>

        <div className="form-grid">
          <Field label="Breitengrad">
            <input type="number" step="0.00001" value={form.latitude} onChange={(event) => set('latitude', event.target.value)} />
          </Field>
          <Field label="Längengrad">
            <input type="number" step="0.00001" value={form.longitude} onChange={(event) => set('longitude', event.target.value)} />
          </Field>
          <Field label="&nbsp;">
            <button className="btn" onClick={locate}>
              <IconSearch width={13} height={13} /> Koordinaten aus Anschrift
            </button>
          </Field>
        </div>

        <span className="section-title">Ansprechpartner</span>
        <div className="form-grid">
          <Field label="Name">
            <input value={form.contactName} onChange={(event) => set('contactName', event.target.value)} />
          </Field>
          <Field label="Telefon">
            <input value={form.contactPhone} onChange={(event) => set('contactPhone', event.target.value)} />
          </Field>
          <Field label="E-Mail">
            <input type="email" value={form.contactEmail} onChange={(event) => set('contactEmail', event.target.value)} />
          </Field>
        </div>

        <Field label="Zugang / Anlieferung" hint="Tor, Schlüssel, Anlieferzeiten – was der Fahrer wissen muss.">
          <textarea value={form.accessNotes} onChange={(event) => set('accessNotes', event.target.value)} />
        </Field>
        <Field label="Notiz">
          <textarea value={form.note} onChange={(event) => set('note', event.target.value)} />
        </Field>

        <div className="row">
          <Toggle
            checked={form.isDefault}
            onChange={(value) => set('isDefault', value)}
            label="Vorgabelager für Wareneingänge"
          />
          <Toggle checked={form.isActive} onChange={(value) => set('isActive', value)} label="Aktiv" />
        </div>
      </div>
    </Modal>
  );
}
